import { useCallback, useState } from 'react';
import { EftposBookingWithMarketDetails } from '../../../Hooks/UseEftposBookings';
import { EftposDepositAccount } from 'shared-components';
import dayjs from 'Abstracts/Time';
import styled from 'styled-components';
import TippyDropdown, { DropdownDivider } from '../../../Abstracts/Components/TippyDropdown';
import { Link } from 'react-router-dom';
import useUserApi from '../../../Services/UserApi';
import { EftposPosListWithItems } from '../../../Hooks/UseEftposPosLists';
import LoadingOverlay from '../../../Abstracts/Components/LoadingOverlay';
import { Colors } from '../../../Abstracts';
import Tippy from '@tippyjs/react';

interface EftposBookingsTableProps {
	bookings: EftposBookingWithMarketDetails[];
	setBookings: React.Dispatch<React.SetStateAction<EftposBookingWithMarketDetails[] | undefined>>;
	depositAccounts: EftposDepositAccount[];
	posLists: EftposPosListWithItems[];
	context: string;
}

export default function EftposBookingsTable(props: EftposBookingsTableProps) {
	const { bookings, setBookings, depositAccounts, posLists, context } = props;
	const [loading, setLoading] = useState(false);

	const userApi = useUserApi();

	const saveBooking = useCallback(
		async (eftposBooking: EftposBookingWithMarketDetails) => {
			setLoading(true);
			try {
				const res = await userApi<{ eftposBooking: EftposBookingWithMarketDetails }>({
					route: 'eftpos/api',
					data: {
						action: 'saveEftposBooking',
						eftposBooking,
					},
					errorMessage: 'Error saving booking',
				});
				return res.data.eftposBooking;
			} finally {
				setLoading(false);
			}
		},
		[userApi],
	);

	const downloadSales = async (bookingId: number) => {
		const res = await userApi<{ file: string }>({
			route: 'eftpos/api',
			data: {
				action: 'downloadSales',
				eftposBookingId: bookingId,
			},
			errorMessage: 'Error downloading sales',
		});

		const hiddenElement = document.createElement('a');
		hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(res.data.file);
		hiddenElement.target = '_blank';
		hiddenElement.download = 'sales.csv';
		hiddenElement.click();
	};

	return (
		<>
			{loading && <LoadingOverlay text="Saving" />}
			<Table className="table table-striped">
				<thead>
					<tr className="header">
						<th>Date</th>
						<th>Market</th>
						<th>Menu</th>
						<th>Deposit account</th>
						<th className="text-right">Sales</th>
					</tr>
				</thead>
				<tbody>
					{bookings
						.sort((a, b) => b.MarketDate - a.MarketDate)
						.map((booking) => {
							const lastUpdated =
								Date.now() / 1000 - (booking.Modified ?? 0) < 2200
									? dayjs.unix(booking.Modified ?? 0).fromNow()
									: dayjs.unix(booking.Modified ?? 0).format('h:mm a, MMM DD, YYYY');

							const salesContent =
								booking.EftposBookingGrossSales === 0 ? (
									'--'
								) : (
									<>
										<span className="gross-sales-label">Sales:</span>
										<div className="gross-sales-container">
											<Tippy content={<SalesBreakdownTooltip booking={booking} />}>
												<div className="gross-sales-value">
													$
													{window.formatCurrency(
														'' +
															(booking.EftposBookingGrossSales -
																booking.EftposBookingRefunds -
																booking.EftposBookingPartialRefunds),
													)}
												</div>
											</Tippy>
											<small>Last updated {lastUpdated}</small>
											<button
												onClick={() => downloadSales(booking.EftposBookingId)}
												className="btn btn-sm btn-blue"
											>
												Download sales
											</button>
										</div>
									</>
								);
							return (
								<tr key={booking.EftposBookingId}>
									<td className="market-date">
										{dayjs.unix(booking.MarketDate).format('MMM DD, YYYY')}
									</td>
									<td className="market-name">{booking.MarketName}</td>
									<MenuCell
										context={context}
										locked={booking.EftposBookingLockedData !== null}
										createRoute={`/menus/create?context=${context}&bookingId=${booking.EftposBookingId}`}
										posListId={booking.EftposBookingEftposPosListId}
										posLists={posLists}
										setNewPosListId={async (newPosListId) => {
											try {
												const newBooking = await saveBooking({
													...booking,
													EftposBookingEftposPosListId: newPosListId,
												});
												setBookings((current) => [
													...(current ?? []).map((b) => {
														return b.EftposBookingId === booking.EftposBookingId
															? newBooking
															: b;
													}),
												]);
											} catch (err) {
												console.log(err);
											}
										}}
									/>
									<DepositAccountCell
										context={context}
										locked={booking.EftposBookingLockedData !== null}
										createRoute={`/deposit-accounts/register?context=${context}&bookingId=${booking.EftposBookingId}`}
										depositAccountId={booking.EftposBookingEftposDepositAccountId}
										depositAccounts={depositAccounts}
										setNewDepositAccountId={async (newDepositAccountId) => {
											try {
												const newBooking = await saveBooking({
													...booking,
													EftposBookingEftposDepositAccountId: newDepositAccountId,
												});
												setBookings((current) => [
													...(current ?? []).map((b) => {
														return b.EftposBookingId === booking.EftposBookingId
															? newBooking
															: b;
													}),
												]);
											} catch (err) {
												console.log(err);
											}
										}}
									/>
									<td className="gross-sales">{salesContent}</td>
								</tr>
							);
						})}
				</tbody>
			</Table>
		</>
	);
}

const Table = styled.table`
	tr {
		.market-date {
			width: 20%;
		}
		.market-name {
			width: 20%;
		}
		.menu-name {
			width: 20%;

			.menu-btn {
				display: flex;
				flex-direction: column;

				.name {
					font-weight: bold;
				}

				.details {
					font-size: 1rem;
				}
			}
		}
		.deposit-account-name {
			width: 20%;

			.deposit-account-btn {
				display: flex;
				flex-direction: column;

				.name {
					font-weight: bold;
				}

				.details {
					font-size: 1rem;
				}
			}
		}
		.gross-sales {
			width: 20%;
			text-align: right;

			.gross-sales-label {
				display: none;
			}

			.gross-sales-container {
				width: 100%;
				display: flex;
				flex-direction: column;
				align-items: flex-end;

				small {
					color: #999;
				}
			}
		}

		.dropdown-label {
			display: none;
		}
	}

	@media (max-width: 768px) {
		tr.header {
			display: none;
		}

		tr {
			display: grid;
			margin-bottom: 10px;
			border: 1px solid #ddd;
			border-radius: 4px;
			padding: 10px;

			grid-template-areas:
				'market-name market-name'
				'market-date market-date'
				'menu-name menu-name'
				'deposit-account deposit-account'
				'gross-sales gross-sales';

			.market-date,
			.market-name,
			.menu-name,
			.gross-sales,
			.deposit-account-name {
				width: 100%;
			}

			.dropdown-label {
				display: block;
			}

			.menu-name,
			.deposit-account-name {
				display: flex;
				align-items: center;
				justify-content: space-between;
			}

			.gross-sales {
				grid-area: gross-sales;
				display: flex;
				align-items: center;
				justify-content: space-between;

				.gross-sales-label {
					text-align: left;
					display: block;
				}
			}

			.market-date,
			.market-name {
				border-top: none;
			}

			.market-date {
				font-size: 1rem;
				grid-area: market-date;
				padding: 4px 0 12px 4px;
			}
			.market-name {
				grid-area: market-name;
				padding: 0 4px;
			}
			.menu-name {
				grid-area: menu-name;
			}
			.deposit-account-name {
				grid-area: deposit-account;
			}
		}
	}
`;

interface MenuCellProps {
	posListId: number | null;
	posLists: EftposPosListWithItems[];
	setNewPosListId: (newPosListId: number) => void;
	createRoute: string;
	context: string;
	locked: boolean;
}

const MenuCell = (props: MenuCellProps) => {
	const { posListId, posLists, createRoute, setNewPosListId, context, locked } = props;
	const [open, setOpen] = useState(false);
	const posList = posLists.find((posList) => posList.EftposPosListId === posListId);
	const cellContent = posList ? (
		<button
			className={`${locked ? 'disabled' : ''} btn btn-blue btn-sm menu-btn`}
			onClick={() => {
				if (locked) return;
				setOpen(!open);
			}}
		>
			<span className="name">{posList.EftposPosListName}</span>
			<span className="details">Items: {posList.items.length}</span>
		</button>
	) : (
		<div style={{ display: 'inline-block' }} onClick={() => setOpen(!open)}>
			<WarningCell>No menu set</WarningCell>
		</div>
	);
	return (
		<td className="menu-name" style={{ padding: '4px' }}>
			<span className="dropdown-label">Menu:</span>
			{locked ? (
				<Tippy content="This booking is now locked and cannot be edited.">{cellContent}</Tippy>
			) : (
				<TippyDropdown
					options={
						<>
							{posLists.map((x) => (
								<MenuDropdownItem key={x.EftposPosListId}>
									{x.EftposPosListId === posListId ? (
										<a className={`dropdown-item active`}>{x.EftposPosListName}</a>
									) : (
										<Tippy content="Use this menu" placement="left">
											<a
												className={`dropdown-item`}
												onClick={() => {
													setNewPosListId(x.EftposPosListId);
													setOpen(false);
												}}
											>
												{x.EftposPosListName}
											</a>
										</Tippy>
									)}
									<Tippy content="Edit menu" placement="right">
										<Link
											className={`edit-button ${x.EftposPosListId === posListId ? 'active' : ''}`}
											to={`/menus/edit/${x.EftposPosListId}?context=${context}`}
										>
											<i className="fas fa-edit" />
										</Link>
									</Tippy>
								</MenuDropdownItem>
							))}
							<DropdownDivider />
							<NewItemButton to={createRoute}>
								<i className="fas fa-plus" />
								<span>Create a new menu</span>
							</NewItemButton>
						</>
					}
					open={open}
					setOpen={setOpen}
				>
					{cellContent}
				</TippyDropdown>
			)}
		</td>
	);
};

const NewItemButton = styled(Link)`
	display: flex;
	min-width: 300px;
	align-items: center;
	justify-content: center;

	span {
		margin-left: 1rem;
	}
`;

const MenuDropdownItem = styled.div`
	display: flex;
	align-items: center;

	.dropdown-item {
		flex-grow: 1;
		padding-right: 3rem;
	}

	.edit-button {
		margin-left: auto;

		background: ${Colors.gray1};

		&:hover {
			background: ${Colors.gray2};
		}

		&.active {
			background: ${Colors.gray3};

			&:hover {
				background: ${Colors.gray4};
				cursor: pointer;
			}
		}
	}
`;

interface DepositAccountCellProps {
	depositAccountId: number | null;
	depositAccounts: EftposDepositAccount[];
	setNewDepositAccountId: (newDepositAccountId: number) => void;
	createRoute: string;
	context: string;
	locked: boolean;
}

const DepositAccountCell = (props: DepositAccountCellProps) => {
	const { depositAccountId, depositAccounts, createRoute, setNewDepositAccountId, context, locked } = props;
	const [open, setOpen] = useState(false);
	const depositAccount = depositAccounts.find((posList) => posList.EftposDepositAccountId === depositAccountId);
	const cellContent = depositAccount ? (
		<button
			className={`${locked ? 'disabled' : ''} btn btn-blue btn-sm deposit-account-btn`}
			onClick={() => {
				if (locked) return;
				setOpen(!open);
			}}
		>
			<span className="name">{depositAccount.EftposDepositAccountName}</span>
			<span className="details">BSB: {depositAccount.EftposDepositAccountBsb}</span>
			<span className="details">Account: {depositAccount.EftposDepositAccountAccount}</span>
		</button>
	) : (
		<div style={{ display: 'inline-block' }} onClick={() => setOpen(!open)}>
			<WarningCell>No deposit account set</WarningCell>
		</div>
	);
	return (
		<td className="deposit-account-name" style={{ padding: '4px' }}>
			<span className="dropdown-label">Deposit account:</span>
			{locked ? (
				<Tippy content="This booking is now locked and cannot be edited.">{cellContent}</Tippy>
			) : (
				<TippyDropdown
					options={
						<>
							{depositAccounts.map((x) => (
								<MenuDropdownItem key={x.EftposDepositAccountId}>
									{x.EftposDepositAccountId === depositAccountId ? (
										<a className={`dropdown-item active`}>{x.EftposDepositAccountName}</a>
									) : (
										<Tippy content="Use this account" placement="left">
											<a
												className={`dropdown-item ${
													x.EftposDepositAccountId === depositAccountId ? 'active' : ''
												}`}
												onClick={() => {
													setNewDepositAccountId(x.EftposDepositAccountId);
													setOpen(false);
												}}
											>
												{x.EftposDepositAccountName}
											</a>
										</Tippy>
									)}
									<Tippy content="Edit account" placement="right">
										<Link
											className={`edit-button ${
												x.EftposDepositAccountId === depositAccountId ? 'active' : ''
											}`}
											to={`/deposit-accounts/edit/${x.EftposDepositAccountId}?context=${context}`}
										>
											<i className="fas fa-edit" />
										</Link>
									</Tippy>
								</MenuDropdownItem>
							))}
							<DropdownDivider />
							<NewItemButton to={createRoute}>
								<i className="fas fa-plus" />
								<span>Register a new account</span>
							</NewItemButton>
						</>
					}
					open={open}
					setOpen={setOpen}
				>
					{cellContent}
				</TippyDropdown>
			)}
		</td>
	);
};

const WarningCell = (props: { children?: React.ReactNode; onClick?: () => void }) => {
	const { children, onClick } = props;
	return (
		<StyledWarningCell onClick={onClick}>
			<i className="fa fa-exclamation-triangle" />
			{children}
		</StyledWarningCell>
	);
};

const StyledWarningCell = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;

	padding: 4px 12px;

	color: #8a6d3b;
	background-color: #fcf8e3;

	border: 1px solid #faebcc;
	border-radius: 4px;

	&:hover {
		cursor: pointer;
	}

	i {
		margin-right: 1rem;
	}
`;

function SalesBreakdownTooltip(props: { booking: EftposBookingWithMarketDetails }) {
	const { booking } = props;
	const printNegative = (val: number) => {
		if (val !== 0) {
			return `$-${window.formatCurrency('' + val)}`;
		} else {
			return `--`;
		}
	};
	const netSales =
		booking.EftposBookingGrossSales - booking.EftposBookingRefunds - booking.EftposBookingPartialRefunds;
	const grossCardSales = booking.EftposBookingGrossSales - booking.EftposBookingCashSales;
	return (
		<SalesBreakdownContainer>
			<div>
				<strong>Card Sales: </strong>
			</div>
			<div className="figure">${window.formatCurrency('' + grossCardSales)}</div>
			<div>
				<strong>Cash Sales: </strong>
			</div>
			<div className="figure">${window.formatCurrency('' + booking.EftposBookingCashSales)}</div>
			<div className="negative">
				<strong>Refunds: </strong>
			</div>
			<div className="figure negative">
				{printNegative(booking.EftposBookingRefunds + booking.EftposBookingPartialRefunds)}
			</div>
			<HorizontalLine />
			<div>
				<strong>Sales: </strong>
			</div>
			<div className="figure">${window.formatCurrency('' + netSales)}</div>
		</SalesBreakdownContainer>
	);
}

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #ccc;
`;

const SalesBreakdownContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.5rem;

	.figure {
		text-align: right;
	}

	.negative {
		color: #919191;
	}

	${HorizontalLine} {
		grid-column: 1 / -1;
	}
`;
